import { getApiBaseUrl } from '../lib/getApiUrl';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CustomFieldDefinition,
  CreateCustomFieldRequest,
  UpdateCustomFieldRequest
} from '@/types/customField';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// API functions
const fetchCustomFields = async (): Promise<CustomFieldDefinition[]> => {
  const response = await fetch(`${API_BASE_URL}/custom-fields`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch custom fields');
  }

  const data = await response.json();
  return data.data || data;
};

const createCustomFieldApi = async (data: CreateCustomFieldRequest): Promise<CustomFieldDefinition> => {
  const response = await fetch(`${API_BASE_URL}/custom-fields`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create custom field');
  }

  const result = await response.json();
  return result.data || result;
};

const updateCustomFieldApi = async (
  id: string,
  data: UpdateCustomFieldRequest
): Promise<CustomFieldDefinition> => {
  const response = await fetch(`${API_BASE_URL}/custom-fields/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update custom field');
  }

  const result = await response.json();
  return result.data || result;
};

const deleteCustomFieldApi = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/custom-fields/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete custom field');
  }
};

const reorderCustomFieldsApi = async (fieldIds: string[]): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/custom-fields/reorder`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fieldIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to reorder custom fields');
  }
};

export interface UseCustomFieldsReturn {
  fields: CustomFieldDefinition[];
  isLoading: boolean;
  error: Error | null;
  createField: (data: CreateCustomFieldRequest) => Promise<void>;
  updateField: (id: string, data: UpdateCustomFieldRequest) => Promise<void>;
  deleteField: (id: string) => Promise<void>;
  reorderFields: (fieldIds: string[]) => Promise<void>;
  refetch: () => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useCustomFields = (): UseCustomFieldsReturn => {
  const queryClient = useQueryClient();

  const {
    data: fields = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customFields'],
    queryFn: fetchCustomFields,
  });

  const createMutation = useMutation({
    mutationFn: createCustomFieldApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomFieldRequest }) =>
      updateCustomFieldApi(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomFieldApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: reorderCustomFieldsApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] });
    },
  });

  const createField = useCallback(async (data: CreateCustomFieldRequest) => {
    await createMutation.mutateAsync(data);
  }, [createMutation]);

  const updateField = useCallback(async (id: string, data: UpdateCustomFieldRequest) => {
    await updateMutation.mutateAsync({ id, data });
  }, [updateMutation]);

  const deleteField = useCallback(async (id: string) => {
    await deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  const reorderFields = useCallback(async (fieldIds: string[]) => {
    await reorderMutation.mutateAsync(fieldIds);
  }, [reorderMutation]);

  return {
    fields,
    isLoading,
    error: error as Error | null,
    createField,
    updateField,
    deleteField,
    reorderFields,
    refetch,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

// Hook for managing custom field values for a specific lead
export interface UseLeadCustomFieldsReturn {
  values: Record<string, any>;
  setValues: (values: Record<string, any>) => void;
  saveValues: () => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: Error | null;
}

export const useLeadCustomFields = (leadId: string): UseLeadCustomFieldsReturn => {
  const queryClient = useQueryClient();
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [isDirty, setIsDirty] = useState(false);

  const {
    data: serverValues = {},
    isLoading,
    error,
  } = useQuery({
    queryKey: ['leadCustomFields', leadId],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}/custom-fields`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch lead custom fields');
      }

      const data = await response.json();
      return data.data || data;
    },
    enabled: !!leadId,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, any>) => {
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}/custom-fields`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ values }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save custom field values');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leadCustomFields', leadId] });
      setIsDirty(false);
    },
  });

  const values = isDirty ? localValues : serverValues;

  const setValues = useCallback((newValues: Record<string, any>) => {
    setLocalValues(newValues);
    setIsDirty(true);
  }, []);

  const saveValues = useCallback(async () => {
    await saveMutation.mutateAsync(localValues);
  }, [saveMutation, localValues]);

  return {
    values,
    setValues,
    saveValues,
    isLoading,
    isSaving: saveMutation.isPending,
    isDirty,
    error: error as Error | null,
  };
};
